import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Tablet, Copy, ExternalLink, Printer } from "lucide-react";
import ChurchToolsSwitcher from "@/components/ChurchToolsSwitcher";

// Kiosk launcher — admin-only setup screen that gives the coordinator the URL
// and QR code they'll open on the lobby tablet. The kiosk itself lives at
// /kiosk/<attendance qr_code> and is a public route (no auth), so once opened
// on the tablet it can stay parked without a login session.

export default function KioskLaunch() {
  const { user, loading } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [code, setCode] = useState("");
  const [orgName, setOrgName] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading || !user) return;
    if (user.is_admin) {
      api.get("/admin/orgs").then((r) => {
        const list = r.data || [];
        setOrgs(list);
        setOrgId((cur) => cur || list[0]?.id || null);
      });
    } else {
      setOrgId(user.org_id);
    }
  }, [user, loading]);

  useEffect(() => {
    if (!orgId) return;
    setBusy(true);
    const params = user?.is_admin ? { org_id: orgId } : {};
    api.get("/att/qr-code", { params })
      .then((r) => { setCode(r.data.code); setOrgName(r.data.org_name || ""); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load kiosk URL."))
      .finally(() => setBusy(false));
  }, [orgId, user]);

  const kioskPath = useMemo(() => (code ? `/kiosk/${code}` : ""), [code]);
  const kioskUrl = useMemo(() => (code ? `${window.location.origin}${kioskPath}` : ""), [code, kioskPath]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(kioskUrl); toast.success("Kiosk URL copied."); }
    catch { toast.error("Couldn't copy — long-press to copy manually."); }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/attendance/login" replace />;
  if (!user.is_admin && !user.is_coordinator && !user.is_org_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-10" data-testid="kiosk-launch-page">
      <div className="mb-6">
        <ChurchToolsSwitcher />
      </div>
      <header className="mb-8">
        <div className="overline mb-1" style={{ color: "var(--gold)" }}>Lobby setup</div>
        <div className="flex items-center gap-3">
          <Tablet size={26} style={{ color: "var(--gold)" }} />
          <h1 className="font-serif-display text-4xl sm:text-5xl" style={{ color: "var(--ink)" }}>
            thedisciple<span style={{ color: "var(--gold)", fontStyle: "italic" }}>.kiosk</span>
          </h1>
        </div>
        <p className="text-sm mt-3 max-w-xl" style={{ color: "var(--ink-2)" }}>
          A single lobby tablet that lets guests choose Attendance, Prayer, or Guest sign-up.
          Open the URL below on the tablet, add it to the home screen, and leave it running.
        </p>

        {user.is_admin && orgs.length > 1 && (
          <div className="mt-6">
            <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Organization</label>
            <select value={orgId || ""} onChange={(e) => setOrgId(e.target.value)} className="px-3 py-2 text-sm" data-testid="kiosk-launch-org-select">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
      </header>

      {busy ? (
        <p style={{ color: "var(--muted)" }}>Loading kiosk URL…</p>
      ) : !code ? (
        <p style={{ color: "var(--muted)" }}>Kiosk isn't ready — set up your attendance QR first.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-start">
          <div className="card-surface p-6" style={{ background: "var(--bg-alt)" }}>
            <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--muted)" }}>Kiosk URL for {orgName}</div>
            <div className="p-3 rounded-md font-mono text-sm break-all mb-4" style={{ background: "var(--bg-deep-alt)", color: "var(--ink)", border: "1px solid var(--border-subtle)" }} data-testid="kiosk-launch-url">
              {kioskUrl}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={copy} className="btn-ghost text-xs" data-testid="kiosk-launch-copy">
                <Copy size={13} /> Copy URL
              </button>
              <Link to={kioskPath} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs" data-testid="kiosk-launch-open">
                <ExternalLink size={13} /> Open kiosk
              </Link>
              <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="kiosk-launch-print">
                <Printer size={13} /> Print QR
              </button>
            </div>
            <div className="mt-8 pt-6 border-t text-sm space-y-2" style={{ borderColor: "var(--border-subtle)", color: "var(--ink-2)" }}>
              <p className="font-medium" style={{ color: "var(--ink)" }}>Set up your lobby tablet:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Open this URL in the tablet's browser (Safari, Chrome).</li>
                <li>Tap "Tap to resume full screen". The kiosk now stays locked: guests only see the kiosk screens, and the back button returns to kiosk home.</li>
                <li>To exit, tap "Exit kiosk" (bottom-right) and enter a church admin's login password.</li>
                <li>Keep the tablet plugged in. The kiosk is designed to stay running all day.</li>
              </ol>
            </div>
            <KioskLockHelp />
          </div>

          <div className="flex flex-col items-center gap-3 p-6 rounded-md" style={{ background: "var(--bg-alt)", border: "1.5px solid var(--gold)", boxShadow: "inset 0 0 0 3px var(--bg-base)" }}>
            <div className="p-4 rounded-md" style={{ background: "#FFFFFF" }}>
              <QRCodeSVG value={kioskUrl} size={180} bgColor="#FFFFFF" fgColor="#0A1B33" level="H" includeMargin={false} />
            </div>
            <div className="text-center">
              <div className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>Scan to launch</div>
              <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: "var(--gold)" }}>Point tablet at this</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KioskLockHelp() {
  const steps = [
    ["iPad: Guided Access", ["Settings → Accessibility → Guided Access → turn it on and set a passcode.", "Open the kiosk in Safari, then triple-click the top (or Home) button.", "Tap Start. To end, triple-click again and enter the passcode."]],
    ["Android: Screen pinning", ["Settings → Security (or Security & privacy) → App pinning → turn it on, with \"Ask for PIN before unpinning\".", "Open the kiosk in Chrome, open Recent apps, tap the Chrome icon → Pin.", "To unpin, hold Back + Overview, then enter the device PIN."]],
  ];
  return (
    <div className="mt-6 p-4 rounded-md" style={{ background: "var(--bg-deep-alt)", border: "1px dashed var(--gold)" }} data-testid="kiosk-lock-help">
      <p className="text-sm font-medium mb-1" style={{ color: "var(--ink)" }}>Lock this tablet (strongest protection)</p>
      <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>A website can't stop someone from typing a new address. Turn on the tablet's built-in lock so the browser can't be left at all.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {steps.map(([title, list]) => (
          <div key={title}>
            <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--gold)" }}>{title}</div>
            <ol className="list-decimal list-inside space-y-1 text-xs" style={{ color: "var(--ink-2)" }}>
              {list.map((s) => <li key={s}>{s}</li>)}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
}
